import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowRight, Phone } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { seoPages } from "../data/seoPages";

export default function SeoPage() {
  const { slug } = useParams();
  const page = seoPages[slug];

  const jsonLd = page
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: page.h1,
        provider: {
          "@type": "MotorcycleDealer",
          name: "TT Motorsports",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Beekstraat 4C",
            addressLocality: "Torhout",
            postalCode: "8820",
            addressCountry: "BE",
          },
          telephone: "+32485741524",
        },
        areaServed: "Torhout, West-Vlaanderen",
        description: page.metaDescription,
      }
    : null;

  useSEO({
    title: page?.seoTitle,
    description: page?.metaDescription,
    keywords: page?.keywords,
    jsonLd,
  });

  if (!page) return <Navigate to="/diensten" replace />;

  return (
    <div data-testid={`seo-page-${page.slug}`} className="bg-[#0A0A0B]">
      {/* Header */}
      <section className="pt-36 pb-14 bg-[#0A0A0B] border-b border-[#2A2B31] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full racing-stripe opacity-[0.04]" />
        <div className="max-w-[1000px] mx-auto px-5 md:px-8 relative">
          <nav className="flex items-center gap-2 text-xs font-mono-tt text-[#5A5C63] mb-5">
            <Link to="/" className="hover:text-[#E10600]">Home</Link>
            <span>/</span>
            <Link to="/diensten" className="hover:text-[#E10600]">Diensten</Link>
            <span>/</span>
            <span className="text-[#9A9CA3]">{page.h1}</span>
          </nav>
          <h1 className="heading-xl text-4xl md:text-6xl text-white">{page.h1}</h1>
          <div className="mt-6 space-y-4 max-w-3xl">
            {page.intro.map((p, i) => (
              <p key={i} className="text-[#B8B9BE] text-lg leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-pad">
        <div className="max-w-[1000px] mx-auto px-5 md:px-8">
          <div className="aspect-[16/9] overflow-hidden border border-[#2A2B31] mb-12">
            <img src={page.image.url} alt={page.image.alt} loading="lazy" className="w-full h-full object-cover" />
          </div>

          <div className="space-y-12">
            {page.sections.map((s, i) => (
              <div key={i}>
                <h2 className="heading-xl text-2xl md:text-4xl text-white mb-5">{s.h2}</h2>
                {s.body.map((p, j) => (
                  <p key={j} className="text-[#B8B9BE] leading-relaxed mb-4">{p}</p>
                ))}
                {s.list && (
                  <ul className="grid sm:grid-cols-2 gap-3 mt-4">
                    {s.list.map((li) => (
                      <li key={li} className="flex items-center gap-2.5 text-[#E6E6E8]">
                        <Check size={17} className="text-[#E10600] shrink-0" /> {li}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 bg-[#141518] border border-[#2A2B31] p-8 md:p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="heading-xl text-2xl md:text-3xl text-white mb-2">Klaar om langs te komen?</h3>
              <p className="text-[#9A9CA3]">Neem contact op of maak eenvoudig online een afspraak bij TT Motorsports in Torhout.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
              <Link to={page.cta.to} data-testid="seo-cta-primary"
                className="bg-[#E10600] hover:bg-[#B80500] text-white font-display uppercase font-700 tracking-wide px-6 py-3.5 flex items-center justify-center gap-2 transition-colors">
                {page.cta.label} <ArrowRight size={17} />
              </Link>
              <a href="tel:0485741524"
                className="border border-[#2A2B31] hover:border-white text-white font-display uppercase font-700 tracking-wide px-6 py-3.5 flex items-center justify-center gap-2 transition-colors">
                <Phone size={17} /> Bel ons
              </a>
            </div>
          </div>

          {/* Related */}
          {page.related?.length > 0 && (
            <div className="mt-14">
              <h2 className="heading-xl text-xl text-white mb-5">Gerelateerde diensten</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {page.related.map((rslug) => {
                  const r = seoPages[rslug];
                  if (!r) return null;
                  return (
                    <Link key={rslug} to={`/${rslug}`} data-testid={`seo-related-${rslug}`}
                      className="group bg-[#141518] border border-[#2A2B31] hover:border-[#E10600]/60 p-5 transition-colors">
                      <p className="font-display uppercase font-700 text-white group-hover:text-[#E10600] transition-colors leading-tight">{r.h1}</p>
                      <span className="text-[#9A9CA3] text-sm flex items-center gap-1 mt-2">Lees meer <ArrowRight size={14} /></span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
